import React from 'react';
import { Box, Typography, Paper, Chip, Grid, Divider, Tooltip, Zoom } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  AssignmentTurnedIn as TaskAchievementIcon,
  AccountTree as CoherenceIcon,
  MenuBook as LexicalIcon,
  Spellcheck as GrammarIcon,
  Speed as FluencyIcon,
  RecordVoiceOver as PronunciationIcon,
  InfoOutlined as InfoIcon,
  Analytics as AnalyticsIcon,
  CheckCircleOutline as SuccessIcon
} from '@mui/icons-material';

const ScoringContainer = styled(Paper)(({ theme }) => ({
  padding: '32px',
  borderRadius: '20px',
  boxShadow: '0 10px 40px rgba(153,108,254,0.12)',
  background: '#FFFFFF',
  border: '1px solid #F0E6FF',
  marginBottom: '32px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '6px',
    background: 'linear-gradient(90deg, #996CFE 0%, #7C4DFF 100%)',
  }
}));

const CriteriaCard = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '24px',
  borderRadius: '16px',
  border: '1px solid #F0F0F0',
  boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(153,108,254,0.12)',
    borderColor: '#E8DFFF',
  },
}));

const ScoreCircle = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #F8F2FF 0%, #E8DFFF 100%)',
  border: '2px solid #996CFE',
  color: '#996CFE',
  fontSize: '24px',
  fontWeight: 800,
  marginBottom: '16px',
  boxShadow: '0 4px 10px rgba(153,108,254,0.2)',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  color: '#996CFE',
  backgroundColor: alpha('#996CFE', 0.1),
  padding: '8px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '12px',
}));

const CriteriaLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#2D3748',
  marginBottom: '12px',
  lineHeight: 1.2,
  minHeight: '2.4em',
  display: 'flex',
  alignItems: 'center',
});

const FeedbackItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  marginBottom: '10px',
  padding: '10px 12px',
  borderRadius: '8px',
  backgroundColor: '#F9FAFB',
  borderLeft: '3px solid #996CFE',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const ConfidenceChip = styled(Chip)(({ confidence }) => {
  const colors = {
    high: { bg: alpha('#4CAF50', 0.1), color: '#2E7D32', border: alpha('#4CAF50', 0.3) },
    medium: { bg: alpha('#FF9800', 0.1), color: '#E65100', border: alpha('#FF9800', 0.3) },
    low: { bg: alpha('#F44336', 0.1), color: '#C62828', border: alpha('#F44336', 0.3) },
  };
  const colorScheme = colors[confidence] || colors.medium;
  
  return {
    backgroundColor: colorScheme.bg,
    color: colorScheme.color,
    border: `1px solid ${colorScheme.border}`,
    fontWeight: 700,
    fontSize: '12px',
    height: '28px',
    '& .MuiChip-label': {
      padding: '0 12px',
    }
  };
});

const getCriteriaIcon = (key) => {
  const icons = {
    task_achievement: <TaskAchievementIcon />,
    coherence_and_cohesion: <CoherenceIcon />,
    lexical_resource: <LexicalIcon />,
    grammatical_range_and_accuracy: <GrammarIcon />,
    fluency_and_coherence: <FluencyIcon />,
    pronunciation: <PronunciationIcon />,
  };
  return icons[key] || <AnalyticsIcon />;
};

const formatCriteriaLabel = (key) => {
  const labels = {
    task_achievement: 'Task Achievement',
    coherence_and_cohesion: 'Coherence & Cohesion',
    lexical_resource: 'Lexical Resource',
    grammatical_range_and_accuracy: 'Grammar Accuracy',
    fluency_and_coherence: 'Fluency & Coherence',
    pronunciation: 'Pronunciation',
  };
  return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const IELTSDetailedScoring = ({ aiScoring, testType, manualOverrideScore = null }) => {
  if (!aiScoring || aiScoring.overall_band == null) return null;

  const { criteria, evaluation_confidence } = aiScoring;
  const hasManualOverride = manualOverrideScore !== null && manualOverrideScore !== undefined && manualOverrideScore !== '';

  return (
    <ScoringContainer>
      {hasManualOverride && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: '14px',
          background: 'linear-gradient(90deg, rgba(255, 248, 225, 0.95) 0%, rgba(255, 243, 224, 0.95) 100%)',
          border: '1px solid rgba(245, 124, 0, 0.28)'
        }}>
          <Typography variant="subtitle2" sx={{ color: '#BF5F00', fontWeight: 800, mb: 0.5 }}>
            Teacher-reviewed final score: {manualOverrideScore}
          </Typography>
          <Typography variant="body2" sx={{ color: '#7A4B00', lineHeight: 1.5 }}>
            AI score and comments are kept below for reference, but the final Writing score was reviewed and overridden by your teacher.
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <AnalyticsIcon sx={{ color: '#996CFE', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1A202C', letterSpacing: '-0.5px' }}>
              Detailed Performance Analysis
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#718096', ml: 5.5 }}>
            Comprehensive breakdown of your score based on IELTS assessment criteria
          </Typography>
        </Box>
        
        {evaluation_confidence && (
          <Tooltip title="Confidence level of the AI evaluation" arrow placement="top">
            <ConfidenceChip
              label={`CONFIDENCE: ${evaluation_confidence.toUpperCase()}`}
              confidence={evaluation_confidence}
            />
          </Tooltip>
        )}
      </Box>

      {criteria && (
        <Grid container spacing={3}>
          {Object.entries(criteria).map(([key, criterionData], index) => {
            const score = typeof criterionData === 'object' && criterionData !== null && 'score' in criterionData 
              ? criterionData.score 
              : typeof criterionData === 'number' 
                ? criterionData 
                : 0;
            const feedback = typeof criterionData === 'object' && criterionData !== null && 'feedback' in criterionData
              ? criterionData.feedback
              : [];

            return (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                  <CriteriaCard elevation={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <IconWrapper>
                        {getCriteriaIcon(key)}
                      </IconWrapper>
                      <ScoreCircle>
                        {score}
                      </ScoreCircle>
                    </Box>
                    
                    <CriteriaLabel variant="subtitle1">
                      {formatCriteriaLabel(key)}
                    </CriteriaLabel>
                    
                    <Divider sx={{ mb: 2, borderColor: '#F0F0F0' }} />
                    
                    <Box sx={{ flexGrow: 1 }}>
                      {Array.isArray(feedback) && feedback.length > 0 ? (
                        feedback.map((item, idx) => (
                          <FeedbackItem key={idx}>
                            <SuccessIcon sx={{ color: '#996CFE', fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: '#4A5568', lineHeight: 1.5, fontSize: '0.85rem' }}>
                              {item}
                            </Typography>
                          </FeedbackItem>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: '#A0AEC0', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                          No specific feedback available
                        </Typography>
                      )}
                    </Box>
                  </CriteriaCard>
                </Zoom>
              </Grid>
            );
          })}
        </Grid>
      )}
    </ScoringContainer>
  );
};

export default IELTSDetailedScoring;
